import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlayerTableViewModelV2,
  GameStateBroadcast,
  GameStateStatus,
  GameOverGameState,
  PlayerModelV2,
  ScoringSetGameState,
  Suite,
} from 'shared';
import { isErrorResponse, V2apiClient } from '../../ApiClient';
import { useApiCall } from '../../useApiCall';
import { useToast } from '../../ui/Toast';
import { Players } from './players';
import { StartButtons } from './start-buttons';
import { BiddingButtons } from './bidding-buttons';
import { CurrentAction } from './current-action';
import { GameInfoPanel } from './game-info-panel';
import { ScoreResultsModal } from './score-results-modal';
import { GameOverModal } from './game-over-modal';
import { PausedModal, PausedSeat } from './paused-modal';
import { Stage } from '../../ui/Stage';
import { GameSettings } from './game-settings';
import { useGameSounds } from './useGameSounds';
import { exitFullscreen } from '../../ui/useFullscreen';
import { GameCodeBadge } from './game-code';
import { HowToPlay } from './how-to-play';
import { TableBackground } from './board/TableBackground';
import { BoardSkeleton } from './board/board-skeleton';
import { TrickArea } from './board/TrickArea';
import { useGameWebSocket } from './useGameWebSocket';
import { ChatWindow } from './chat/chat-window';
import { ConnectionBanner } from './connection-banner';
import { SpectatingBadge } from './spectating-badge';
import { TrumpReveal } from './trump-reveal';
import { LastTrickData } from './last-trick-peek';

interface Props {
  player?: PlayerModelV2;
}

const isGameStateBroadcast = (value: unknown): value is GameStateBroadcast =>
  !!value && typeof value === 'object' && (value as { type?: unknown }).type === 'gameState';

export const Setback = (props: Props) => {
  const { lastMessage } = useGameWebSocket();
  const navigate = useNavigate();
  const toast = useToast();
  const apiCall = useApiCall();

  const [table, setTable] = useState<PlayerTableViewModelV2>();
  const [resultsModalOpen, setResultsModalOpen] = useState<boolean>(false);
  // Host chose "wait for a new player": keep the pause modal dismissed until a
  // fresh pause begins (reset below when the table is no longer paused).
  const [pauseDismissed, setPauseDismissed] = useState<boolean>(false);
  const [fillingSeat, setFillingSeat] = useState<boolean>(false);
  // Trump-reveal flourish: holds the suit to flash when trump changes (new set).
  const [trumpReveal, setTrumpReveal] = useState<Suite>();
  // The previous completed trick, for the "Last trick" peek.
  const [lastTrick, setLastTrick] = useState<LastTrickData>();
  const api = V2apiClient;

  useGameSounds(table);

  // The trump suit exists once a set is underway (StartingTrick / Playing / …).
  const trumpSuite: Suite | undefined = (() => {
    const gs = table?.gameState as { trumpSuite?: Suite } | undefined;
    return gs && 'trumpSuite' in gs ? gs.trumpSuite : undefined;
  })();

  // Flash the trump flourish whenever the trump suit changes (a fresh set).
  useEffect(() => {
    if (!trumpSuite) return;
    setTrumpReveal(trumpSuite);
    const t = window.setTimeout(() => setTrumpReveal(undefined), 1500);
    return () => window.clearTimeout(t);
  }, [trumpSuite]);

  // Capture the trick as it's scored (full cards + a flagged winner) for the
  // "Last trick" peek; clear it between games so it never shows a stale trick.
  useEffect(() => {
    const gs = table?.gameState;
    if (!gs || gs.status === GameStateStatus.GameOver) {
      setLastTrick(undefined);
      return;
    }
    if (gs.status === GameStateStatus.ScoringTrick && table?.tableCards?.length) {
      const winner = table.tableCards.find((c) => c.isWinner);
      const winnerName = table.tablePlayers.find((p) => p?.id === winner?.playerId)?.name;
      setLastTrick({ cards: table.tableCards, winnerName });
    }
  }, [table]);

  useEffect(() => {
    getGameState();
  }, []);

  // Leaving the game page (leave button, disband, or any navigation away) drops
  // fullscreen and releases the landscape lock, so the lobby isn't stuck rotated
  // in fullscreen on mobile.
  useEffect(() => {
    return () => {
      void exitFullscreen();
    };
  }, []);

  useEffect(() => {
    if (!table?.isPaused) setPauseDismissed(false);
  }, [table?.isPaused]);

  const fillSeatWithComputer = async (seatIndex: number) => {
    setFillingSeat(true);
    await apiCall(api.fillSeatWithComputer(seatIndex));
    setFillingSeat(false);
  };

  // The server pushes a full snapshot after every action, so the board only
  // fetches on mount and when told the table is gone ("getUpdatedGame", which
  // then 404s and bows a spectator out to the lobby).
  useEffect(() => {
    const data = lastMessage?.data;
    if (!data) return;
    if (data === 'getUpdatedGame') {
      getGameState();
      return;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(data);
    } catch {
      return;
    }
    if (isGameStateBroadcast(parsed)) applyTable(parsed.state);
  }, [lastMessage]);

  const applyTable = (next: PlayerTableViewModelV2) => {
    setTable(next);
    if (
      next.gameState?.status === GameStateStatus.Bidding &&
      table?.gameState?.status === GameStateStatus.Playing
    ) {
      setResultsModalOpen(true);
    }
  };

  const getGameState = () => {
    api
      .getGameState()
      .then((response) => {
        if (!isErrorResponse(response)) {
          applyTable(response);
          return;
        }
        toast.error(response.error);
        // A spectator whose table was disbanded gets a "not at a table" error
        // on the next refresh — there's nothing left to watch, so bow out to
        // the lobby. (Seated players keep the board through transient blips.)
        if (table?.isSpectator) navigate('/lobby');
      })
      .catch(() => toast.error('Could not reach the server.'));
  };

  if (!table || !props.player) {
    return <BoardSkeleton />;
  }

  const status = table.gameState?.status;
  const spectating = !!table.isSpectator;
  // For a spectator, slot 0 is seat 0 (not them), so it never confers a turn.
  const localIsCurrent = !spectating && !!table.tablePlayers[0]?.isCurrentTurn;
  // Reserved seats (a player left, or disconnected and may reconnect), tagged
  // with their real server seat index so the host's "replace with computer"
  // request targets the right seat.
  const pausedSeats: PausedSeat[] = table.tablePlayers
    .map((p, i) =>
      p && (p.isVacant || p.isDisconnected)
        ? {
            seatIndex: table.seatIndexes[i],
            name: p.name,
            reason: p.isDisconnected ? ('disconnected' as const) : ('left' as const),
          }
        : null,
    )
    .filter((s): s is PausedSeat => s !== null);
  const showTrick = !!table.gameState && !!table.tableCards && status !== GameStateStatus.Bidding;

  return (
    <>
      <ConnectionBanner />

      <Stage>
        <TableBackground />

        <Players table={table} />

        {showTrick && (
          <TrickArea
            tableCards={table.tableCards!}
            tablePlayers={table.tablePlayers}
            scoring={status === GameStateStatus.ScoringTrick}
          />
        )}

        {trumpReveal && <TrumpReveal suite={trumpReveal} />}

        {spectating && <SpectatingBadge />}

        {/* Pre-game the name + code sit in their own badge; during play they're
            shown in the GameInfoPanel header. Either way always visible to share. */}
        {!table.gameState && <GameCodeBadge code={table.tableCode} title={table.tableTitle} />}

        {!table.gameState ? (
          // Spectators don't get the host/waiting start panel — they're just
          // watching until the host starts.
          !spectating && <StartButtons table={table} />
        ) : (
          <>
            <CurrentAction table={table} />
            <GameInfoPanel table={table} lastTrick={lastTrick} />

            {status === GameStateStatus.Bidding && localIsCurrent && (
              <BiddingButtons table={table} />
            )}
          </>
        )}

        <GameSettings
          youAreCreator={table.youAreCreator}
          isLocked={table.isLocked}
          isSpectator={spectating}
        />
        <HowToPlay />
      </Stage>

      <ChatWindow />

      {resultsModalOpen && (table.gameState as ScoringSetGameState)?.setResults && (
        <ScoreResultsModal
          open={resultsModalOpen}
          onClose={() => setResultsModalOpen(false)}
          gameState={table.gameState as ScoringSetGameState}
        />
      )}

      {/* Spectators watch the game-over screen but can't ready-up for a rematch. */}
      {table.gameState?.status === GameStateStatus.GameOver && !spectating && (
        <GameOverModal
          open
          gameState={table.gameState as GameOverGameState}
          tablePlayers={table.tablePlayers}
          currentPlayerId={props.player.id}
          onReady={() => void apiCall(api.playerReady())}
        />
      )}

      {table.isPaused && (
        <PausedModal
          open={!pauseDismissed}
          isCreator={table.youAreCreator}
          pausedSeats={pausedSeats}
          busy={fillingSeat}
          onFillWithComputer={fillSeatWithComputer}
          onWait={() => setPauseDismissed(true)}
        />
      )}
    </>
  );
};
